import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import api from '../../services/api';
import { toast } from 'sonner';
import { Upload, FileText, Download, Trash2, Eye, Lock } from 'lucide-react';

export function DocumentsTab({ documents, eventRef, setDocuments }) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const uploadFile = async (file, visibility = 'private') => {
    if (!file || !eventRef) { toast.error('Save the event first before uploading documents'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('visibility', visibility);
      await api.post(`/api/rfx/events/${eventRef}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${file.name} uploaded`);
      const r = await api.get(`/api/rfx/events/${eventRef}`);
      setDocuments(r.data.documents || []);
    } catch { toast.error('Upload failed'); }
    setUploading(false);
  };

  const handleFileInput = (e) => {
    const files = e.target.files;
    if (files) Array.from(files).forEach(f => uploadFile(f, 'private'));
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = e.dataTransfer.files;
    if (files) Array.from(files).forEach(f => uploadFile(f, 'private'));
  };

  const toggleVisibility = async (fileName, currentVis) => {
    const newVis = currentVis === 'public' ? 'private' : 'public';
    try {
      await api.put(`/api/rfx/events/${eventRef}/documents/${fileName}/visibility`, { visibility: newVis });
      setDocuments(documents.map(d => d.file_name === fileName ? { ...d, visibility: newVis } : d));
      toast.success(`${fileName} set to ${newVis}`);
    } catch { toast.error('Failed to update visibility'); }
  };

  const handleDelete = async (fileName) => {
    if (!eventRef) return;
    try {
      await api.delete(`/api/rfx/events/${eventRef}/documents/${fileName}`);
      setDocuments(documents.filter(d => d.file_name !== fileName));
      toast.success('Document removed');
    } catch { toast.error('Delete failed'); }
  };

  const handleDownload = async (fileName) => {
    try {
      const r = await api.get(`/api/rfx/events/${eventRef}/documents/${fileName}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a'); a.href = url; a.download = fileName; a.click();
    } catch { toast.error('Download failed'); }
  };

  const publicCount = documents.filter(d => d.visibility === 'public').length;
  const privateCount = documents.length - publicCount;

  return (
    <div className="space-y-4 py-2" data-testid="documents-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold">Supporting Documents ({documents.length})</h3>
          {documents.length > 0 && (
            <p className="text-xs text-slate-500 mt-0.5">
              {publicCount} public (visible to suppliers) · {privateCount} private (internal only)
            </p>
          )}
        </div>
      </div>

      {!eventRef ? (
        <p className="text-xs text-amber-600">Save the event first to enable document uploads.</p>
      ) : (
        <div
          onDrop={handleDrop}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onClick={() => document.getElementById('doc-upload-input').click()}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); document.getElementById('doc-upload-input').click(); } }}
          tabIndex={0}
          role="button"
          aria-label="Upload documents — drag and drop or click to browse"
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
            dragOver ? 'border-indigo-500 bg-indigo-50 scale-[1.01]' : 'border-slate-300 hover:border-indigo-400 hover:bg-slate-50'
          }`}
          data-testid="doc-drop-zone"
        >
          <input type="file" id="doc-upload-input" className="hidden" onChange={handleFileInput} multiple aria-label="Upload documents" />
          <Upload className={`w-8 h-8 mx-auto mb-2 ${dragOver ? 'text-indigo-500' : 'text-slate-400'}`} />
          {uploading ? (
            <p className="text-sm text-indigo-600 font-medium">Uploading...</p>
          ) : (
            <>
              <p className="text-sm font-medium text-slate-700">{dragOver ? 'Drop files here' : 'Drag & drop files here'}</p>
              <p className="text-xs text-slate-400 mt-1">or click to browse — PDF, Word, Excel, images, etc.</p>
            </>
          )}
        </div>
      )}

      {documents.length > 0 && (
        <div className="border rounded-lg divide-y">
          {documents.map((d, i) => (
            <div key={i} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors" data-testid={`doc-row-${i}`}>
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <div className="min-w-0">
                  <span className="text-sm font-medium truncate block">{d.file_name}</span>
                  <span className="text-[10px] text-slate-400">{d.uploaded_by}</span>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => toggleVisibility(d.file_name, d.visibility || 'private')}
                  className={`flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full border transition-colors ${
                    d.visibility === 'public'
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-300 hover:bg-emerald-100'
                      : 'bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100'
                  }`}
                  title={d.visibility === 'public' ? 'Visible to suppliers — click to make private' : 'Internal only — click to make public'}
                  data-testid={`doc-visibility-${i}`}
                >
                  {d.visibility === 'public' ? <><Eye className="w-3 h-3" /> Public</> : <><Lock className="w-3 h-3" /> Private</>}
                </button>
                <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => handleDownload(d.file_name)} title="Download">
                  <Download className="w-3.5 h-3.5" />
                </Button>
                <Button size="sm" variant="ghost" className="h-7 px-2 text-red-400 hover:text-red-600" onClick={() => handleDelete(d.file_name)} title="Delete">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
